/**
 * DevAuth0Provider — replaces Auth0Provider in agentic/local dev mode.
 *
 * When VITE_AGENTIC=true, this provider:
 * - Skips Auth0 entirely (no redirects, no JWKS, no token refresh)
 * - Injects a dev token (VITE_DEV_TOKEN) into all API requests
 * - Reports isAuthenticated=true, provides mock user info
 * - Enables the app to run on any port without Auth0 redirect_uri constraints
 *
 * The dev token is a cleartext "dev-<base64json>" token that the local-lambda
 * authorizer recognizes and processes without Auth0 JWT validation.
 */
import { Auth0Context } from "@auth0/auth0-react"
import { FC, PropsWithChildren } from "react"

const DEV_TOKEN = import.meta.env.VITE_DEV_TOKEN || ""

// Parse the dev token payload for user info
function parseDevTokenPayload(): Record<string, any> {
  try {
    if (!DEV_TOKEN.startsWith("dev-")) return {}
    return JSON.parse(atob(DEV_TOKEN.slice(4)))
  } catch {
    return {}
  }
}

const payload = parseDevTokenPayload()

const mockAuth0Value = {
  isAuthenticated: true,
  isLoading: false,
  user: {
    sub: payload.sub || "dev-user",
    email: payload.email || "dev@localhost",
    name: `${payload.firstName || "Dev"} ${payload.lastName || "User"}`,
    nickname: payload.email?.split("@")[0] || "dev",
    picture: "",
    email_verified: true,
    updated_at: new Date().toISOString(),
  },
  loginWithRedirect: async () => {},
  loginWithPopup: async () => {},
  logout: () => { window.location.href = "/login" },
  getAccessTokenSilently: async () => DEV_TOKEN,
  getAccessTokenWithPopup: async () => DEV_TOKEN,
  getIdTokenClaims: async () => undefined,
  handleRedirectCallback: async () => ({ appState: {} }),
  error: undefined,
} as any

export const DevAuth0Provider: FC<PropsWithChildren> = ({ children }) => {
  return (
    <Auth0Context.Provider value={mockAuth0Value}>
      {children}
    </Auth0Context.Provider>
  )
}
